import { Inject, Injectable, LOCALE_ID, NgZone } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { merge, skip, Subject } from "rxjs";
import { ApiService, Countries, Mover } from "./api.service";
import { SubmitEmbedInfo, SubmitUserData, TrackingService } from './tracking.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from "../../environments/environment";
import domainData from '../../assets/json/environment.json';
import countryData from '../../assets/json/environment-by-country.json';
import emailRelayCountries from '../../assets/json/email-relay.json';
import timezoneData from '../../assets/json/timezones-to-countries.json';
import Bugsnag from "@bugsnag/js";
import { fallbackCountries } from './country-fallback';
import { ApiRequestError } from './log-sanitiser';
import { parsePrematchMovers } from './prematch-response';
import { GooglePlacesService, PlacesTelemetry } from './google-places.service';
import { AffiliateConfigService } from './affiliate-config.service';
import { TelemetryService } from './telemetry.service';
import { AddressSource, browserSignals, diffFlags, FLAG_CONTROLS, formFlags, nextAddressSource, plannedSteps, sessionContext } from './telemetry-context';
import { failureDetails, prematchTiming, responseStart, submitOutcome } from './submit-telemetry';
import { InteractionTracker, InteractionType } from './interaction-tracker';
import { Question } from '../post-submit/third-party-questions/question/question.model';
import { orderThirdPartyQuestions, questionControlKeys } from '../post-submit/third-party-questions/third-party-questions.util';

@Injectable({
    providedIn: 'root'
})
export class FormService {

	public formActive : boolean = true;
	public formLoading : boolean = false;
	public fccLoading : boolean = false;
	public questionsActive : boolean = false;
	public questions : any | undefined;
	// DEV-6874: the third-party questions are shown one per step; index into `questions`
	public currentQuestionIndex : number = 0;
	public postSubmitStep : string = 'thankYou';
	public aboutToRedirect : boolean = false;
	public failSafeMessage : boolean = false;
	public dashboardUrl : string = '';
	/** Dashboard URL from postRequest response; used as primary source in countDownAndRedirect */
	private responseDashboardUrl : string = '';
	public token : string = '';
	public lastResubmitPhoneTime: number | null = null;
	public showNote : boolean = false;
	public countries !: Countries;
	public popularCountries !: Countries;
	public beforeUnloadListener: any;
	public domainData: any;
	public vcCategories: any | undefined;

	get isAffiliateMode(): boolean {

		return this.affiliateConfigService?.isAffiliateMode ?? false;
	}

	// DEV-6542/DEV-7491/DEV-7571: flag hiding Sir Leo imagery, shared by every
	// component that renders a Sir Leo asset (background, mobile header, volume
	// calculator). DEV-7895: hidden by default on every form, including domains
	// without an entry and affiliate forms; a domain opts back in with
	// "disableSirLeo": false.
	get disableSirLeo(): boolean {

		return this.getSetting( 'disableSirLeo' ) !== false;
	}
	public movingToExtendedOptionalLabelsStartingValue = false;
	// Initial steps amount | Don't count dynamic steps
	public totalStepsAmount: number = 6;

	// The current step number
	public currentStep : number = 1;
	public currentFormGroup : UntypedFormGroup;
	public currentFormGroupName : string = 'movingFrom';

	// The state of the form
	public isSubmitting = false;

	/** When true, step validation is skipped so you can click through to any step. */
	private skipStepValidation = false;

	// The current state of the progress bar
	public progress : number = 15;

	// Time spent tracking: when current step was entered (ms since epoch)
	private stepEnteredAt: number | null = null;

	// "<stepEnteredAt>:<trigger>" of the abandonments logged since the page was last visible (DEV-7864)
	private abandonLogged = new Set<string>();

	// Whether form_loaded has been sent for this page (DEV-7892)
	private formLoadedLogged = false;

	// "<stepEnteredAt>:<step>" of the visits that already sent step_viewed (DEV-7892)
	private stepViewLogged = new Set<string>();

	// The step the visitor submitted from: events after the submit are logged on it,
	// since 'finished' is no step (DEV-7892)
	private submittedStepName = 'nameEmailPhone';

	// The session context sent on form_loaded, repeated on submit_clicked (DEV-7892)
	private sessionSnapshot: Record<string, unknown> = {};

	// Form-variant flags as last sent, the base of form_flags_changed (DEV-7892)
	private loggedFlags: Record<string, boolean> = {};

	// Clicks, taps and key presses per step, and the session's first interaction (DEV-7892)
	private interactionTracker = new InteractionTracker( document, ( type, ms ) => this.logFirstInteraction( type, ms ) );

    subject = new Subject<any>();
    public mainForm: UntypedFormGroup = new UntypedFormGroup({
        movingFrom: new UntypedFormGroup({
            address: new UntypedFormControl('', [Validators.required]),
            city: new UntypedFormControl('', [Validators.required]),
            postcode: new UntypedFormControl('', [Validators.required]),
            country: new UntypedFormControl(null, [Validators.required]),
            state: new UntypedFormControl(''),
	        _houseNumber: new UntypedFormControl( '' ), // This is for the postcode api
            _progress: new UntypedFormControl(15),
            _next: new UntypedFormControl('movingTo'),
	        _previous: new UntypedFormControl( false ),
	        _doneTracking: new UntypedFormControl( true ),
            _shouldShow: new UntypedFormControl(true),
	        _initiallyFocusAddress: new UntypedFormControl( false ),
	        _googleApiUsed: new UntypedFormControl( false ),
	        _addressSource: new UntypedFormControl( null ), // DEV-7892: how the address was filled in, see nextAddressSource()
	        _timespent: new UntypedFormControl( 0 )
        }),
	    propertyTypeFrom: new UntypedFormGroup({
			propertyType: new UntypedFormControl( '' ),
		    m2: new UntypedFormControl( '' ),
		    propertyRooms: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(20),
		    _next: new UntypedFormControl( 'movingTo' ),
		    _previous: new UntypedFormControl( 'movingFrom' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
		    _timespent: new UntypedFormControl( 0 )
	    }),
	    // Only shown if propertyTypeFrom.propertyType = apartment/flat
	    propertyTypeDetailsFrom: new UntypedFormGroup( {
		    floor: new UntypedFormControl( '' ),
		    elevator: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(25),
		    _next: new UntypedFormControl( 'movingTo' ),
		    _previous: new UntypedFormControl( 'propertyTypeFrom' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
		    _timespent: new UntypedFormControl( 0 )
	    }),
        movingTo: new UntypedFormGroup({
            city: new UntypedFormControl('', [Validators.required]),
            country: new UntypedFormControl(null, [Validators.required]),
	        address: new UntypedFormControl( '' ),
	        postcode: new UntypedFormControl( '' ),
	        state: new UntypedFormControl(''),
	        _isExtended: new UntypedFormControl( false ),
	        _extendedOptional: new UntypedFormControl( false ),
            _progress: new UntypedFormControl(40),
            _next: new UntypedFormControl('movingSize'),
	        _previous: new UntypedFormControl( 'movingFrom' ),
	        _doneTracking: new UntypedFormControl( false ),
            _shouldShow: new UntypedFormControl( false ),
	        _googleApiUsed: new UntypedFormControl( false ),
	        _addressSource: new UntypedFormControl( null ), // DEV-7892: how the address was filled in, see nextAddressSource()
	        _timespent: new UntypedFormControl( 0 )
        }),
	    propertyTypeTo: new UntypedFormGroup({
		    propertyType: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(45),
		    _next: new UntypedFormControl( 'movingSize' ),
		    _previous: new UntypedFormControl( 'movingTo' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
		    _timespent: new UntypedFormControl( 0 )
	    }),
	    // Only shown if propertyTypeFrom.propertyType = apartment/flat
	    propertyTypeDetailsTo: new UntypedFormGroup( {
		    floor: new UntypedFormControl( '' ),
		    elevator: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(50),
		    _next: new UntypedFormControl( 'movingSize' ),
		    _previous: new UntypedFormControl( 'propertyTypeTo' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
		    _timespent: new UntypedFormControl( 0 )
	    }),
        movingSize: new UntypedFormGroup({
            movingSize: new UntypedFormControl('', [Validators.required]),
	        _bedroomsActive: new UntypedFormControl( false ),
            _progress: new UntypedFormControl(55),
            _next: new UntypedFormControl('movingContent'),
	        _previous: new UntypedFormControl( 'movingTo' ),
	        _doneTracking: new UntypedFormControl( false ),
            _shouldShow: new UntypedFormControl(false),
	        _timespent: new UntypedFormControl( 0 )
        }),
        movingContent: new UntypedFormGroup({
            movingContent: new UntypedFormControl('' ),
	        volumeM3: new UntypedFormControl( 0 ),
	        volumeFt3: new UntypedFormControl( 0 ),
	        boxesSmall: new UntypedFormControl( 0 ),
	        boxesMedium: new UntypedFormControl( 0 ),
	        boxesLarge: new UntypedFormControl( 0 ),
	        customItems: new UntypedFormControl( '' ),
	        _volumeCalculator: new UntypedFormControl( ''  ),
            _progress: new UntypedFormControl(65),
            _next: new UntypedFormControl('movingDate'),
	        _previous: new UntypedFormControl( 'movingSize' ),
	        _doneTracking: new UntypedFormControl( false ),
            _shouldShow: new UntypedFormControl(false),
	        _timespent: new UntypedFormControl( 0 )
        }),
	    movingBoxes: new UntypedFormGroup({
		    boxesSmall: new UntypedFormControl( 0 ),
		    boxesMedium: new UntypedFormControl( 0 ),
		    boxesLarge: new UntypedFormControl( 0 ),
		    customItems: new UntypedFormControl( '' ),
		    volumeM3: new UntypedFormControl( 0 ),
		    volumeFt3: new UntypedFormControl( 0 ),
		    _progress: new UntypedFormControl(65),
		    _next: new UntypedFormControl('uploadPictures'),
		    _previous: new UntypedFormControl( 'movingSize' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false),
		    _timespent: new UntypedFormControl( 0 )
	    }),
	    uploadPictures: new UntypedFormGroup({
		    files: new UntypedFormControl([]),
		    _uploadSessionId: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(70),
		    _next: new UntypedFormControl('movingDate'),
		    _previous: new UntypedFormControl('movingContent'),
		    _doneTracking: new UntypedFormControl(false),
		    _shouldShow: new UntypedFormControl(false),
		    _timespent: new UntypedFormControl( 0 )
	    }),
        movingDate: new UntypedFormGroup({
            movingDate: new UntypedFormControl('', [Validators.required]),
            _progress: new UntypedFormControl(80),
            _next: new UntypedFormControl('nameEmailPhone'),
	        _previous: new UntypedFormControl( 'uploadPictures' ),
	        _shouldShow: new UntypedFormControl(false),
	        _doneTracking: new UntypedFormControl( false ),
	        _timespent: new UntypedFormControl( 0 )
        }),
	    consentMovers: new UntypedFormGroup({
		    movers: new UntypedFormArray([]),
		    _originalMovers: new UntypedFormArray([]),
		    _progress: new UntypedFormControl(85),
		    _next: new UntypedFormControl('nameEmailPhone'),
		    _previous: new UntypedFormControl('movingDate'),
		    _shouldShow: new UntypedFormControl(false),
		    _doneTracking: new UntypedFormControl(false),
		    _timespent: new UntypedFormControl( 0 )
	    }),
		nameEmailPhone: new UntypedFormGroup({
			name: new UntypedFormControl( '', [Validators.required]),
			phoneNumber: new UntypedFormControl('', [Validators.required]),
			phoneNumberPlaceholder: new UntypedFormControl('', []),
			phoneNumberComplete: new UntypedFormControl( '' ),
			email: new UntypedFormControl('', [Validators.required]),
			nonExclusiveLead: new UntypedFormControl( true ),
			// DEV-6936: email-relay opt-in. Defaults to false and is flipped to true
			// together with _emailRelayOptInVisible in verifyEmailRelayOptIn(), so a
			// consumer who never saw the checkbox never submits an opt-in. The WP
			// plugin only forwards emailRelayOptIn to the ERP when the visibility
			// flag is true, which keeps the ERP's three states intact:
			// NULL = never offered, false = offered and declined, true = opted in.
			emailRelayOptIn: new UntypedFormControl( false ),
			_emailRelayOptInVisible: new UntypedFormControl( false ),
			_progress: new UntypedFormControl(90),
			_next: new UntypedFormControl('finished'),
			_previous: new UntypedFormControl( 'movingDate' ),
			_shouldShow: new UntypedFormControl(false ),
			_doneTracking: new UntypedFormControl( false ),
			_timespent: new UntypedFormControl( 0 )
		}),
	    // UTM tags or Cookies
	    _ca: new UntypedFormControl( '' ),
	    _so: new UntypedFormControl( '' ),
	    _gclid: new UntypedFormControl( '' ),
	    _gbraid: new UntypedFormControl( '' ),
	    _wbraid: new UntypedFormControl( '' ),
	    _oppref: new UntypedFormControl( '' ),
	    _fbclid: new UntypedFormControl( '' ),
	    _msclkid: new UntypedFormControl( '' ),
	    _landing_query: new UntypedFormControl( '' ),
	    _firstpage: new UntypedFormControl( '' ),
	    _lastpage: new UntypedFormControl( '' ),
	    _referrer: new UntypedFormControl( '' ),
	    _worker_host: new UntypedFormControl( '' ),
	    _worker_path: new UntypedFormControl( '' ),
	    _worker_qs: new UntypedFormControl( '' ),
	    _worker_referrer: new UntypedFormControl( '' ),
	    _worker_ts: new UntypedFormControl( '' ),
	    _vcToken: new UntypedFormControl( '' ),
	    _afToken: new UntypedFormControl( '' ),
	    _customerId: new UntypedFormControl( '' ),
	    _customerName: new UntypedFormControl( '' ),
	    _language: new UntypedFormControl( this.locale.split('-')[0] ),

	    _isIframe: new UntypedFormControl( false ),

	    // Not used?
	    _exclusiveLead: new UntypedFormControl( '' ),

	    _propertyTypeFromEnabled: new UntypedFormControl( false  ),
	    _propertyTypeDetailsFromEnabled: new UntypedFormControl( false ),
	    _propertyTypeToEnabled: new UntypedFormControl( false  ),
	    _propertyTypeDetailsToEnabled: new UntypedFormControl( false ),
	    _uploadPicturesEnabled: new UntypedFormControl( false ),

	    _movingContentOrMovingBoxes: new UntypedFormControl( '' ),

	    _m2Enabled: new UntypedFormControl( false ),
	    _propertyRoomsEnabled: new UntypedFormControl( false ),
	    _consentMoversEnabled: new UntypedFormControl( false ),
	    _fccEnabled: new UntypedFormControl( false ),
	    _prematchDuration: new UntypedFormControl( 0 ),

		_otpEnabled: new UntypedFormControl( false ),
    });
	public thirdPartyForm: UntypedFormGroup = new UntypedFormGroup( {
		// Dynamic controls based on what questions we need to show
	});
	public verificationForm: UntypedFormGroup = new UntypedFormGroup( {
		otp: new UntypedFormControl( '', [Validators.required, Validators.minLength( 6 ), Validators.maxLength( 6 )] ),
		_token: new UntypedFormControl( '' )
	});
	public volumeCalculatorForm: UntypedFormGroup = new UntypedFormGroup( {
		// Dynamic controls based on what the api gives us
	});

    constructor(private apiService: ApiService,
                private trackingService: TrackingService,
                private cookieService: CookieService,
				@Inject(LOCALE_ID) public locale: string,
				private googlePlacesService: GooglePlacesService,
				private affiliateConfigService: AffiliateConfigService,
				private telemetryService: TelemetryService,
				private ngZone: NgZone
    ) {

		// Set initial form group
		this.currentFormGroup = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;

		// Start timer for initial step
		this.stepEnteredAt = Date.now();

        // Services with injectable can't do their own ngOnInit
        this.init();

		// Telemetry (DEV-7864): attribution on every event, and an event when the visitor leaves mid-step
		this.telemetryService.setContextProvider( () => this.telemetryAttribution() );
		this.listenForAbandonment();
		this.googlePlacesService.telemetry$.subscribe( event => this.logPlacesEvent( event ) );
    }

    init(): void {

	    let hostname = window.location.hostname;
	    let domain = hostname.replace( 'www.', '' );

		if( ! environment.production && ! environment.staging ) {

			domain = 'sea.localhost';
		}

		// Subdomains to main
		if( domain.includes( 'sirelo.org' ) && ! domainData.hasOwnProperty( domain ) ) {

			domain = 'sirelo.org';
		}

		this.domainData = domainData[domain as keyof typeof domainData];

		if( this.affiliateConfigService.config ) {

			this.domainData = this.affiliateConfigService.config;
		}

		// Set value of otp enabled based on domainData
		this.mainForm.controls['_otpEnabled'].setValue( !!this.getSetting( 'verificationEnabled' ) );

		// Load countries from the local asset, or the bundled fallback if that fails
		const countriesStart = performance.now();
		const logCountriesMs = () => this.logContextUpdated( {}, { countries_ms: Math.round( performance.now() - countriesStart ) } );

		this.apiService.getCountriesFromAsset().subscribe( {
			next: countries => {

				this.setCountries( countries );
				logCountriesMs();
			},

			// DEV-7522: without a country list both dropdowns stay empty and the visitor
			// cannot continue. Use the bundled list instead. The asset is static and served
			// next to main.js, so once the form has loaded a failure is a crawler being
			// throttled or a visitor who already left - nothing we can fix, and the visitor
			// never notices. Report only a broken build: a 404 (asset missing) or a 200 whose
			// body is not valid JSON (asset corrupt) - Angular reports the latter with its 2xx status.
			error: ( error: unknown ) => {

				this.setCountries( fallbackCountries( this.locale ) );
				logCountriesMs();

				const brokenInBuild = error instanceof ApiRequestError
					&& [ 'status 404', 'status 200' ].includes( error.failure );

				if( brokenInBuild ) {

					Bugsnag.notify( error as Error, event => {

						event.severity = 'warning';
					} );
				}
			}
		} );

		// For all step0 Data
		this.handleStep0Data();

		// Attempt to set the country by browser timezone
		this.setCountryByTimezone();

		// DEV-6493: track an affiliate-partner-form visit once per load (affiliate mode only).
		// init() runs once from this root singleton's constructor, so no extra de-dupe is needed.
		// Fire-and-forget — ApiService swallows errors so tracking never affects the form.
		if( this.affiliateConfigService.isAffiliateMode ) {

			this.apiService.postFormVisit( this.affiliateConfigService.affiliateToken ).subscribe();
		}
    }

	// Fill both country dropdowns; popular countries come from the domain codes
	private setCountries( countries: Countries ) {

		this.countries = countries;
		const popularCodes: string[] = ( this.domainData as any )?.popularCountryCodes ?? [];
		this.popularCountries = countries.filter( c => popularCodes.includes( c.country_code ) );
	}

	// This function gets executed on the start of the form
	public handleStep0Data() {

		var movingFrom = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;
		var movingTo = this.mainForm.get( 'movingTo' ) as UntypedFormGroup;

		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);

		var placeFrom = urlParams.get( 'place_from' );
		var placeTo = urlParams.get( 'place_to' );

		const hasValue = (varToCheck:any) => {

			return varToCheck && varToCheck.length > 0;
		};

		if( placeFrom && placeFrom.length > 0 ) {

			// At this point we know that the Google api was used on step 0
			movingFrom.controls['_googleApiUsed'].setValue( true );
			movingFrom.controls['_addressSource'].setValue( nextAddressSource( null, 'prefill' ) );

			var addressFrom = urlParams.get( 'route_from' );
			var streetNumberFrom = urlParams.get( 'street_number_from' );
			var postalCodeFrom = urlParams.get( 'postal_code_from' );
			var localityFrom = urlParams.get( 'locality_from' );
			var postalTownFrom = urlParams.get( 'postal_town_from' );
			var countryFrom = urlParams.get( 'country_from' );

			// Attempt to get AddressFrom from the url
			if( hasValue(addressFrom) ) {

				// Add HouseNumber to Street with space or add a space after the street
				addressFrom += hasValue(streetNumberFrom) ? ' '+streetNumberFrom : ' ';

				movingFrom.controls['address'].setValue( addressFrom );
			}

			// Attempt to get postcodeFrom from the url
			if( hasValue(postalCodeFrom) ) {

				movingFrom.controls['postcode'].setValue( postalCodeFrom );
			}

			// Attempt to get cityFrom/localityFrom from the url
			if( hasValue(localityFrom) ) {

				movingFrom.controls['city'].setValue( localityFrom );
			}
			else if( hasValue(postalTownFrom) ) {

				// Get the cityFrom from the postalTownFrom instead
				movingFrom.controls['city'].setValue( postalTownFrom );
			}

			// Attempt to get the countryFrom from the url
			if( hasValue(countryFrom) ) {

				movingFrom.controls['country'].setValue( countryFrom );
				movingFrom.controls['country'].markAsDirty();
			}

			// Has a postcode
			// Has a city
			// Has a country
			// Is missing the whole address or just the streetNumber
			if( hasValue(postalCodeFrom) && hasValue(localityFrom) && hasValue(countryFrom) &&
				( ! hasValue( addressFrom ) || ! hasValue( streetNumberFrom ) ) ) {

				// Set focus as either address or houseNumber is still missing
				movingFrom.controls['_initiallyFocusAddress'].setValue( true );
			}
		}

		if( placeTo && placeTo.length > 0 ) {

			// At this point we know that the Google api was used on step 0
			movingTo.controls['_googleApiUsed'].setValue( true );
			movingTo.controls['_addressSource'].setValue( nextAddressSource( null, 'prefill' ) );

			var addressTo = urlParams.get( 'route_to' );
			var streetNumberTo = urlParams.get( 'street_number_to' );
			var postalCodeTo = urlParams.get( 'postal_code_to' );
			var localityTo = urlParams.get( 'locality_to' );
			var postalTownTo = urlParams.get( 'postal_town_to' );
			var countryTo = urlParams.get( 'country_to' );

			// Attempt to get AddressTo from the url
			if( hasValue(addressTo) ) {

				// Add HouseNumber to Street with space or add a space after the street
				addressTo += hasValue(streetNumberTo) ? ' '+streetNumberTo : ' ';

				movingTo.controls['address'].setValue( addressTo );
				movingTo.controls['_isExtended'].setValue( true );
			}

			// Attempt to get postcodeTo from the url
			if( hasValue(postalCodeTo) ) {

				movingTo.controls['postcode'].setValue( postalCodeTo );
				movingTo.controls['_isExtended'].setValue( true );
			}

			// Attempt to get cityTo/localityTo from the url
			if( hasValue(localityTo) ) {

				movingTo.controls['city'].setValue( localityTo );
			}
			else if( hasValue(postalTownTo) ) {

				// Get the cityTo from the postalTownTo instead
				movingTo.controls['city'].setValue( postalTownTo );
			}

			// Attempt to get the countryTo from the url
			if( hasValue(countryTo) ) {

				movingTo.controls['country'].setValue( countryTo );
				movingTo.controls['country'].markAsDirty();
			}
		}

		var paramSo = urlParams.get( 'so' );
		var paramCa = urlParams.get( 'ca' );
		var paramGclid = urlParams.get( 'gclid' );
		var paramGbraid = urlParams.get( 'gbraid' );
		var paramWbraid = urlParams.get( 'wbraid' );
		var paramOppref = urlParams.get( 'oppref' );
		var paramFbclid = urlParams.get( 'fbclid' );
		var paramMsclkid = urlParams.get( 'msclkid' );

		// If the "so" parameter is set, use it
		if( hasValue(paramSo) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_so'].setValue( paramSo );
		}
		else {

			const soCookieValue = this.cookieService.get('socookie');

			if( hasValue(soCookieValue) ) {

				// Set the Cookie value
				(<UntypedFormGroup>this.mainForm ).controls['_so'].setValue( soCookieValue );
			}
		}

		// If the "ca" parameter is set, use it
		if( hasValue(paramCa) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_ca'].setValue( paramCa );
		}
		else {

			const caCookieValue = this.cookieService.get('cacookie');

			if( hasValue(caCookieValue) ) {

				// Set the Cookie value
				(<UntypedFormGroup>this.mainForm ).controls['_ca'].setValue( caCookieValue );
			}
		}

		const landingQueryCookie = this.cookieService.get( 'landing_query' );

		if( hasValue(landingQueryCookie) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_landing_query'].setValue( landingQueryCookie );
		}

		// Get gclid from url
		if( hasValue(paramGclid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_gclid'].setValue( paramGclid );
		}
		else {

			// Get gclid from cookie
			const gclidCookieValue = this.cookieService.get('gclid');

			if( hasValue(gclidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_gclid'].setValue( gclidCookieValue );
			}
		}

		if( hasValue(paramGbraid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_gbraid'].setValue( paramGbraid );
		}
		else {

			const gbraidCookieValue = this.cookieService.get('gbraid');

			if( hasValue(gbraidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_gbraid'].setValue( gbraidCookieValue );
			}
		}

		if( hasValue(paramWbraid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_wbraid'].setValue( paramWbraid );
		}
		else {

			const wbraidCookieValue = this.cookieService.get('wbraid');

			if( hasValue(wbraidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_wbraid'].setValue( wbraidCookieValue );
			}
		}

		if( hasValue(paramOppref) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_oppref'].setValue( paramOppref );
		}
		else {

			const opprefCookieValue = this.cookieService.get('oppref');

			if( hasValue(opprefCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_oppref'].setValue( opprefCookieValue );
			}
		}

		// Get fbclid from url
		if( hasValue(paramFbclid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_fbclid'].setValue( paramFbclid );
		}
		else {

			// Get fbclid from cookie
			const fbclidCookieValue = this.cookieService.get('fbclid');

			if( hasValue(fbclidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_fbclid'].setValue( fbclidCookieValue );
			}
		}

		// Get msclkid from url
		if( hasValue(paramMsclkid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_msclkid'].setValue( paramMsclkid );
		}
		else {

			// Get msclkid from cookie
			const msclkidCookieValue = this.cookieService.get('msclkid');

			if( hasValue(msclkidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_msclkid'].setValue( msclkidCookieValue );
			}
		}

		// Get firstpage from cookie
		const firstpageCookieValue = this.cookieService.get('firstpage');

		if( hasValue(firstpageCookieValue) ) {

			// Pass it to the ERP
			(<UntypedFormGroup>this.mainForm ).controls['_firstpage'].setValue( firstpageCookieValue );
		}

		// Get lastpage from cookie
		const lastpageCookieValue = this.cookieService.get('lastpage');

		if( hasValue(lastpageCookieValue) ) {

			// Pass it to the ERP
			(<UntypedFormGroup>this.mainForm ).controls['_lastpage'].setValue( lastpageCookieValue );
		}

		const referrerCookieValue = this.cookieService.get('referrer');

		if( hasValue(referrerCookieValue) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_referrer'].setValue( referrerCookieValue );
		}

		// DEV-7862: the ad click the Cloudflare Worker captured at the edge (tg_w_* cookies).
		// Passed raw; the ERP decides which tracking source to trust.
		const workerCookies = {
			_worker_host:     'tg_w_host',
			_worker_path:     'tg_w_path',
			_worker_qs:       'tg_w_qs',
			_worker_referrer: 'tg_w_ref',
			_worker_ts:       'tg_w_ts',
		};

		for( const [control, cookieName] of Object.entries( workerCookies ) ) {

			const workerCookieValue = this.cookieService.get( cookieName );

			if( hasValue(workerCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls[control].setValue( workerCookieValue );
			}
		}

		var paramVcToken = urlParams.get( 'vc' );

		if( hasValue(paramVcToken) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_vcToken'].setValue( paramVcToken );
		}

		if( this.affiliateConfigService.isAffiliateMode ) {

			(<UntypedFormGroup>this.mainForm ).controls['_afToken'].setValue( this.affiliateConfigService.affiliateToken );
		}

		var paramCustomer = urlParams.get( 'customer' );

		if( hasValue(paramCustomer) ) {

			// Expected output
			interface CustomerResponse {
				origin_id: number;
				company_name: string;
			}

			// Get customer from the API
			this.apiService.getCustomer( paramCustomer! ).subscribe( (response: CustomerResponse) => {

				if( response ) {

					(<UntypedFormGroup>this.mainForm ).controls['_customerId'].setValue( response.origin_id );
					(<UntypedFormGroup>this.mainForm ).controls['_customerName'].setValue( response.company_name );

					// Telemetry: the partner arrives after form_loaded (DEV-7892)
					this.logContextUpdated( { customer_id: response.origin_id, customer_name: response.company_name } );

					const bodyTag = document.body;
					bodyTag.classList.add('reduced-spacing-last-step');
				}
			} );
		}

		var paramInPage = urlParams.get( 'in-page' );

		if( hasValue(paramInPage) ) {

			const html = document.getElementsByTagName('html')[0];
			html.classList.add('in-page');
			let node: any = document.createElement('script');
			node.src = 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.2/iframeResizer.contentWindow.min.js';
			node.type = 'text/javascript';
			node.async = true;
			node.charset = 'utf-8';
			document.getElementsByTagName('head')[0].appendChild(node);

			(<UntypedFormGroup>this.mainForm ).controls['_isIframe'].setValue( true );

			// Don't focus address just in case
			(<UntypedFormGroup>this.mainForm.controls['movingFrom'] ).controls['_initiallyFocusAddress'].setValue( false );
		}
		// If not in page
		else {

			// Make the browser give a confirmation alert before the user can close this page
			// This gets removed again when the users arrive at the thank-you message

			this.launchExitConfirmation();
		}

		// This needs to go before PropertyRoomsFieldCheck
		// Because the rooms field check needs to know if the property type is enabled or not
		this.initialPropertyTypeCheck();

		// This checks for the env value for movingTo to be extended
		// Now separated from propertyTypeCheck :)
		this.initialMovingToExtendedCheck();

		// Check the data from step 0 and if the field is enabled
		this.initialM2FieldCheck();

		// Check if we can enable the house size field
		this.initialPropertyRoomsFieldCheck();
	}

	// Submitting of a form group
    public submitFormGroup( formGroup: UntypedFormGroup ) {

		formGroup = this.currentFormGroup;

		// Telemetry: capture the forward "Continue" click intent (DEV-6453)
		// before the validity guard, so blocked/invalid attempts on intermediate
		// steps are visible (mirrors 'submit_clicked' on the final step). The
		// final step is excluded here — its click is logged as 'submit_clicked'
		// via submitMainForm() — to avoid double-counting.
		if( formGroup.controls['_next'].value !== 'finished' ) {

			// DEV-7864: name the controls that block the click, and the time on the step so far
			// Building the fields reads the whole form: never let that block the click (DEV-7892)
			try {

				// One walk of the controls: the blocking paths are the keys of their reasons
				const reasons = this.telemetryService.invalidControlReasons( formGroup );

				this.logFormAction( 'step_submit_clicked', this.currentFormGroupName, formGroup, undefined, {
					...this.stepTimings( formGroup, false ),
					invalidFields: Object.keys( reasons ),
					fields:        { invalid_reasons: reasons },
				} );

			} catch {
				// Never surface telemetry errors to the form.
			}
		}

	    // If valid (skip when skipStepValidation is true for easier dev navigation)
        if( ! this.skipStepValidation && ! formGroup.valid ) {

            return;
        }

		// Clear note
	    this.showNote = false;

		// Not the submit button click
	    if( formGroup.controls['_next'].value !== 'finished' ) {

			// === DYNAMIC STEPS START === //
			// Here we can change the previous and next step based on values
		    // Don't change steps after this part

			this.verifyDynamicSteps();

			// === END OF DYNAMIC STEPS === //

			this.next();
		}
		else {

			this.progress = 100;
		}
    }

	private recordStepTime( leavingFormGroup: UntypedFormGroup ): void {

		if( this.stepEnteredAt !== null && leavingFormGroup.contains( '_timespent' ) ) {

			const elapsed = Math.round( Date.now() - this.stepEnteredAt );
			const current = leavingFormGroup.controls['_timespent'].value ?? 0;
			leavingFormGroup.controls['_timespent'].setValue( current + elapsed );
		}
	}

	/**
	 * Telemetry timings of a step (DEV-7864): this visit (`durationMs`) and
	 * all visits (`timespentMs`, the `_timespent` control). Pass `recorded`
	 * when recordStepTime() already added this visit to `_timespent`.
	 */
	private stepTimings( formGroup: UntypedFormGroup, recorded: boolean ): { durationMs?: number, timespentMs?: number } {

		const durationMs = this.stepEnteredAt !== null ? Date.now() - this.stepEnteredAt : undefined;

		if( ! formGroup.contains( '_timespent' ) ) {

			return { durationMs };
		}

		const timespent = formGroup.controls['_timespent'].value ?? 0;

		return { durationMs, timespentMs: recorded ? timespent : timespent + ( durationMs ?? 0 ) };
	}

	/**
	 * This function handles the mechanics of going to the next step
	 * It does not check if steps are valid
	 * 
	 * skipStepIncrement is used when we are removing a step that we are currently on.
	 * The slider index should not increase in that case.
	 */
	public next( skipStepIncrement = false ) {

		var formGroup = this.currentFormGroup;

		// Snapshot the leaving step's identity + entry time so we can emit a
		// telemetry event (DEV-6453) before any mutation hides this group.
		const leavingStepName = this.currentFormGroupName;
		const leavingStepIndex = this.currentStep;
		const leavingStepEnteredAt = this.stepEnteredAt;

		// Record time spent on current step before leaving
		this.recordStepTime( formGroup );

		// Fire-and-forget pre-submission telemetry for the just-completed step.
		// Never blocks navigation; never throws.
		this.telemetryService.emitStepCompleted( {
			stepName:    leavingStepName,
			stepIndex:   leavingStepIndex,
			formGroup,
			durationMs:  leavingStepEnteredAt !== null ? ( Date.now() - leavingStepEnteredAt ) : 0,
			timespentMs: this.stepTimings( formGroup, true ).timespentMs,
			locale:      this.locale,
			gclid:       (<UntypedFormControl> this.mainForm.controls['_gclid'] ).value || '',
			extra:       { interactions: this.interactionTracker.takeStepCounts(), ...this.addressSourceField( leavingStepName ) },
		} );

		// Get the name of the next form Group
		var nextFormGroupName = formGroup.controls['_next'].value;

		// Get next form group
		var nextFormGroup = (<UntypedFormGroup>this.mainForm.controls[nextFormGroupName]);

		// Add one to current step / causes animation
		if( ! skipStepIncrement ) {

			this.currentStep++;
		}

		this.currentFormGroup = nextFormGroup;
		this.currentFormGroupName = nextFormGroupName;

		// Hide old step
		formGroup.controls['_shouldShow'].setValue( false );

		// Show new step
		nextFormGroup.controls['_shouldShow'].setValue( true );

		// Scroll to top of window
		window.scrollTo(0,0);

		// For when the form is in an iframe
		if( 'parentIFrame' in window ) {

			// @ts-ignore
			parentIFrame.sendMessage( 'scroll-to-top-of-iframe' );
		}

		// Emit an event for any other code to use the new step name to do things
		// This is also done in going back to previous steps in backMainForm
		this.emitStepChange( this.currentFormGroupName );

		// Do tracking step change
		// We do the tracking for viewing the step (not for completing it)
		// Only do tracking once per step
		if( ! nextFormGroup.controls['_doneTracking'].value ) {

			// Set progress to old/current progress percentage value
			this.progress = nextFormGroup.controls['_progress'].value

			this.trackingService.setStepWithName( nextFormGroupName );

			nextFormGroup.controls['_doneTracking'].setValue( true );
		}

		// Start timer for new step
		this.stepEnteredAt = Date.now();

		this.logStepViewed();
	}

	// Submitting of the whole form
	public submitMainForm() {

		// Testing purposes
		if( ! environment.production && ! environment.staging ) {

			console.log( 'form values', this.mainForm.value );
		}

		// Fire-and-forget telemetry for the final submit click (DEV-6453),
		// before validation/guard returns so we capture click intent even when
		// the attempt is invalid or never reaches the server. The successful
		// POST is logged separately server-side (DEV-6458/6459) via the shared
		// X-Form-Session-Id, so a 'submit_clicked' with no matching submit-log
		// row reveals drop-off at the button itself.
		// DEV-7864: the whole form is validated here, so the blocking controls are dotted paths.
		// Building the fields reads the whole form: never let that block the click (DEV-7892)
		try {

			const reasons = this.telemetryService.invalidControlReasons( this.mainForm );

			this.logFormAction( 'submit_clicked', this.currentFormGroupName, this.currentFormGroup, undefined, {
				...this.stepTimings( this.currentFormGroup, false ),
				invalidFields: Object.keys( reasons ),
				fields:        {
					invalid_reasons: reasons,
					// DEV-7892: the session context once more, in case the form_loaded beacon was lost
					session:         this.sessionSnapshot,
					timings:         this.prematchTimings(),
				},
			} );

		} catch {
			// Never surface telemetry errors to the form.
		}

		// Check if fields are valid (skip when skipStepValidation for dev)
		if( ! this.skipStepValidation && ! this.mainForm.valid ) {

			// Do something to show the error
			(<UntypedFormGroup>this.mainForm).markAllAsTouched();

			return;
		}

		// Already busy
		if( this.isSubmitting ) {

			return;
		}

		// Set to busy
		this.isSubmitting = true;

		// Record time spent on current (last) step before finishing
		this.recordStepTime( this.currentFormGroup );

		// Telemetry: emit the contact step as a completed step (DEV-6453).
		// next() is never called for this step because its _next === 'finished',
		// so emitStepCompleted() must be fired here explicitly — mirroring what
		// next() does for every intermediate step. Without this the contact step
		// (name/email/phone) never appears in the telemetry log.
		// Snapshot the entry time before any mutation hides this group.
		const contactEnteredAt = this.stepEnteredAt;
		this.telemetryService.emitStepCompleted( {
			stepName:    this.currentFormGroupName,
			stepIndex:   this.currentStep,
			formGroup:   this.currentFormGroup,
			durationMs:  contactEnteredAt !== null ? ( Date.now() - contactEnteredAt ) : 0,
			timespentMs: this.stepTimings( this.currentFormGroup, true ).timespentMs,
			locale:      this.locale,
			gclid:       (<UntypedFormControl> this.mainForm.controls['_gclid'] ).value || '',
			extra:       { interactions: this.interactionTracker.takeStepCounts() },
		} );

		// Set next form group
		this.submittedStepName = this.currentFormGroupName;
		this.currentFormGroupName = 'finished';

		// Add the data to any potential bugsnag report. The raw landing query and
		// Worker click data stay out: Bugsnag is an external service and a query
		// string can carry personal data (DEV-7862; #40 review).
		const { _landing_query, _worker_qs, _worker_referrer, ...bugsnagLead } = this.mainForm.value;
		Bugsnag.addMetadata( 'Lead', bugsnagLead );

		// Put the request in here
		var body = JSON.stringify( this.mainForm.value );

		// Start loading
		this.formLoading = true;

		// Transition to the post-submit shell after 1 second.
		setTimeout( () => {

			// Show thank you page after 1 seconds
			this.formActive = false;
			this.formLoading = false;

			// Remove before unload event
			removeEventListener("beforeunload", this.beforeUnloadListener, {capture: true});

			// Check if we are an iframe
			if( 'parentIFrame' in window ) {

				// @ts-ignore
				parentIFrame.sendMessage( 'scroll-to-top-of-iframe' );
			}
		}, 1000 );

		// Post to super-form. FormSessionInterceptor adds the telemetry correlation
		// id (DEV-6453/DEV-7864) so the backend request-validation log (DEV-6459)
		// joins with our pre-submission telemetry events.
		const submitStartedAt = performance.now();

		this.apiService.postRequest(body).subscribe({ next: (response) => {

			//@TODO: Move fake loading to real loading


			if( ! environment.production && ! environment.staging ) {

				console.log( 'raw response: '  );
				console.log( response );
			}

			// Parse response
			response = JSON.parse( response );

			this.logSubmitResult( response, submitStartedAt );

			if( response === 'missing_required_fields' ) {

				// Don't need to do more
				return;
			}

			// ERP was unreachable — lead is stored locally and will be retried by the drip tray.
			// Skip verification (no OTP was sent). On localhost the response also includes mock
			// questions so the questions flow can be debugged; proceedPostSubmitFlow picks them up.
			// The retryWhen on postRequest won't fire because this is a 200 response.
			if( response.saved_failed_lead === true ) {

				if( response.questions ) {
					this.questions = Object.values( response.questions );
				}

				this.proceedPostSubmitFlow();
				this.isSubmitting = false;
				return;
			}

			// Testing purposes
			if( ! environment.production && ! environment.staging ) {

				console.log( 'parsed response: '  );
				console.log( response );
			}

			const contactStep = this.mainForm.get( 'nameEmailPhone' ) as UntypedFormGroup;

			// If successful
			if( 'token' in response && response.token.length > 0 ) {

				this.token = response.token;
				this.responseDashboardUrl = (response.dashboard_url && response.dashboard_url.trim()) ? response.dashboard_url.trim() : '';

				// Store dashboardUrl from response if available
				if( 'dashboardUrl' in response && response.dashboardUrl.length > 0 ) {

					this.dashboardUrl = response.dashboardUrl;
				}

				// Get moving Size for statistics
				var movingSize = (<UntypedFormGroup>this.mainForm.controls['movingSize']).controls['movingSize'].value;
				var movingFrom = (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value;
				var movingTo = (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value;
				var paramCa = this.mainForm.controls['_ca'].value;
				var destinationType = ( movingFrom === movingTo ) ? 2 : 1;
				var userData: SubmitUserData = {
					email: contactStep.get( 'email' )?.value ?? '',
					phone: contactStep.get( 'phoneNumberComplete' )?.value ?? '',
				};

				var embed: SubmitEmbedInfo | undefined = this.mainForm.controls['_isIframe'].value
					? { formToken: this.affiliateConfigService.affiliateToken }
					: undefined;

				// Only do tracking when successful
				this.trackingService.onSubmitForm( response.token, movingSize, destinationType, paramCa, userData, embed );
			}

			// DEV-6936: report the email-relay opt-in state so opt-in rate is
			// measurable per market. true/false when the checkbox was shown, null
			// when it was not (origin country not enabled for this move type, or
			// mover profile) — mirroring the three states the
			// ERP stores, so keep this keyed on the visibility flag and not on the
			// opt-in value, which is false in both the declined and never-shown case.
			this.trackEventWithContext( 'Request - Submit', 'mainForm', {
				emailRelayOptIn: contactStep.controls['_emailRelayOptInVisible'].value
					? !! contactStep.controls['emailRelayOptIn'].value
					: null,
			} );

			// If there are questions in the response
			if( response.questions ) {

				// Make them loop-able for the code
				this.questions = Object.values( response.questions );
			}
			else {

				this.questions = undefined;
			}

			if( this.getSetting('verificationEnabled') ) {

				this.questionsActive = false;
				this.setupVerificationForm();
				this.postSubmitStep = 'verification';
			}
			else {

				this.proceedPostSubmitFlow();
			}

			this.isSubmitting = false;

		}, error: ( error: unknown ) => {

			const submitMs = performance.now() - submitStartedAt;
			this.logEvent( 'submit_failed', {
				failure: failureDetails( error, submitMs ),
				timings: { submit_ms: Math.round( submitMs ) },
			} );

			// HTTP-level failure (network down, unexpected 5xx that got past retries).
			// By this point retries have been exhausted (~10s), so the postSubmitTimer has
			// already fired and formActive is false. Just show the fallback thank-you message.
			this.isSubmitting = false;
			this.formLoading = false;
			this.formActive = false;
			this.failSafeMessage = true;

		}} );
	}

	private stepChangeSubject = new Subject<string>();
	stepChange$ = this.stepChangeSubject.asObservable();

	private emitStepChange( formGroupName: string) {

		this.stepChangeSubject.next( formGroupName );
	}

	/**
	 * Fire a pre-submission telemetry event (DEV-6453) for a discrete user
	 * action that is not a forward step-completion — e.g. going back a step
	 * or a post-submit verification button. Fills in the session-level
	 * context (step index, locale, gclid) the same way next() does. Pass a
	 * formGroup only when its values add context. `extra` carries the
	 * optional DEV-7864 fields (timings, invalid fields, trigger) and, in
	 * `fields`, the DEV-7892 top-level fields. Never throws.
	 */
	public logFormAction(
		action: string,
		stepName: string = this.currentFormGroupName,
		formGroup?: UntypedFormGroup,
		values?: Record<string, unknown>,
		extra: { durationMs?: number, timespentMs?: number, invalidFields?: string[], trigger?: string, fields?: Record<string, unknown> } = {},
	): void {

		try {

			const { fields, ...timingsAndContext } = extra;
			const step = stepName === 'finished' ? this.submittedStepName : stepName;

			this.telemetryService.emitAction( {
				action,
				stepName:  step,
				stepIndex: this.currentStep,
				formGroup,
				values,
				...timingsAndContext,
				extra:     { ...this.addressSourceField( step ), ...fields },
				locale:    this.locale,
				gclid:     (<UntypedFormControl> this.mainForm.controls['_gclid'] ).value || '',
			} );

		} catch {
			// Never surface telemetry errors to the form.
		}
	}

	/**
	 * Start the telemetry session (DEV-7892): one `form_loaded` per form session,
	 * carrying what stays the same for the whole session (attribution, page
	 * URLs, device and bot signals), the load time and the form variant. Later
	 * events join it by form_session_id. Called by AppComponent once the form
	 * is on screen; the work runs in the next macrotask, off the first paint.
	 */
	public logFormLoaded(): void {

		if( this.formLoadedLogged ) {

			return;
		}

		this.formLoadedLogged = true;

		setTimeout( () => {

			try {

				this.sessionSnapshot = { ...sessionContext( this.mainForm, window ), ...browserSignals( window ) };
				this.loggedFlags = formFlags( this.mainForm );

				// One form_loaded per form session: a reload in the same tab keeps the session id (DEV-7892 review)
				if( this.telemetryService.firstInSession( 'form_loaded' ) ) {

					this.logEvent( 'form_loaded', {
						session:       this.sessionSnapshot,
						timings:       { load_ms: Math.round( performance.now() ) },
						form_flags:    this.loggedFlags,
						planned_steps: plannedSteps( this.mainForm ),
					} );
				}

				this.logStepViewed();

				// Only the flag controls: the whole form's valueChanges fires on every keystroke
				const flagControls = Object.values( FLAG_CONTROLS ).map( path => this.mainForm.get( path ) ).filter( control => control !== null );
				merge( ...flagControls.map( control => control!.valueChanges ) ).subscribe( () => this.logFlagChanges() );

				// The counters need no change detection: listen outside Angular's zone
				this.ngZone.runOutsideAngular( () => this.interactionTracker.start() );

			} catch {
				// Never surface telemetry errors to the form.
			}
		}, 0 );
	}

	/** A telemetry event with DEV-7892 fields only, on the current step unless given (DEV-7892). */
	private logEvent( action: string, fields: Record<string, unknown>, stepName: string = this.currentFormGroupName ): void {

		this.logFormAction( action, stepName, undefined, undefined, { fields } );
	}

	/**
	 * Send what the submit call returned (DEV-7892): `submit_response` for a lead
	 * (token or saved failed lead), `submit_no_token` with the response start
	 * otherwise. Both carry the time from click to answer.
	 */
	private logSubmitResult( response: unknown, submitStartedAt: number ): void {

		const timings = { submit_ms: Math.round( performance.now() - submitStartedAt ) };

		if( submitOutcome( response ) === 'no_token' ) {

			this.logEvent( 'submit_no_token', { failure: { response: responseStart( response ) }, timings } );
			return;
		}

		this.logEvent( 'submit_response', { timings } );
	}

	/** The verified-movers (prematch) call failed or returned no list (DEV-7892). */
	private logPrematchFailed( failure: Record<string, string | number>, prematchMs: number ): void {

		this.logEvent( 'prematch_failed', { failure, timings: { prematch_ms: Math.round( prematchMs ) } } );
	}

	/** `prematch_ms` when a prematch ran this session, else nothing (DEV-7892). */
	private prematchTimings(): Record<string, number> | undefined {

		return prematchTiming( this.mainForm.controls['_prematchDuration']?.value );
	}

	/**
	 * The visitor typed in an address field of `groupName` (DEV-7892): the
	 * address is now typed, or a prefilled address edited. Only a change is
	 * written, so later keystrokes do not run the form's value updates again.
	 */
	public markAddressInput( groupName: 'movingFrom' | 'movingTo' ): void {

		const source = this.mainForm.get( groupName + '._addressSource' );
		const next = nextAddressSource( source?.value as AddressSource | null, 'user_input' );

		if( source && source.value !== next ) {
			source.setValue( next );
		}
	}

	/** `{ address_source }` for an address step whose source is known, else `{}` (DEV-7892). */
	private addressSourceField( stepName: string ): { address_source?: AddressSource } {

		const source = stepName === 'movingFrom' || stepName === 'movingTo'
			? this.mainForm.get( stepName + '._addressSource' )?.value
			: null;

		return source ? { address_source: source } : {};
	}

	/** Send a Google Places pick or failure (DEV-7892), on the address step it belongs to. */
	private logPlacesEvent( event: PlacesTelemetry ): void {

		if( event.type === 'error' ) {

			this.logEvent( 'places_error', { places: { stage: event.stage, message: event.message } } );
			return;
		}

		event.formGroup.get( '_addressSource' )?.setValue( nextAddressSource( null, 'places' ) );

		const stepName = event.formGroup === this.mainForm.get( 'movingTo' ) ? 'movingTo' : 'movingFrom';
		const components = Object.fromEntries( Object.entries( event.components ).map( ( [ key, value ] ) => [ 'component_' + key, value ] ) );

		this.logEvent( 'places_suggestion_selected', {
			places: {
				place_id:          event.placeId,
				formatted_address: event.formattedAddress,
				...components,
				filled_fields:     event.filledFields.join( ',' ),
				empty_fields:      event.emptyFields.join( ',' ),
				suggestions_count: event.suggestionsCount,
			},
			timings: { places_ms: event.placesMs },
		}, stepName );
	}

	/** The session's first click, tap or key press, and how long after loading it came (DEV-7892). */
	private logFirstInteraction( type: InteractionType, ms: number ): void {

		this.logEvent( 'first_interaction', {
			session: { interaction_type: type },
			timings: { first_interaction_ms: Math.round( ms ) },
		} );
	}

	/**
	 * Log the form-variant flags that changed since they were last sent
	 * (DEV-7892), with the steps now planned. The base is the snapshot of
	 * form_loaded, so flags set while the form starts are not sent twice.
	 */
	private logFlagChanges(): void {

		try {

			const flags = formFlags( this.mainForm );
			const changed = diffFlags( this.loggedFlags, flags );

			if( Object.keys( changed ).length === 0 ) {

				return;
			}

			this.loggedFlags = flags;
			this.logEvent( 'form_flags_changed', { form_flags: changed, planned_steps: plannedSteps( this.mainForm ) } );

		} catch {
			// Never surface telemetry errors to the form.
		}
	}

	/**
	 * Log that the current step is on screen (DEV-7892), once per visit. Sent
	 * after the step is rendered (next macrotask), so navigation never waits
	 * on it. Before form_loaded nothing is sent: that event logs the first step.
	 */
	private logStepViewed(): void {

		const stepName = this.currentFormGroupName;
		const key = this.stepEnteredAt + ':' + stepName;

		if( ! this.formLoadedLogged || this.stepEnteredAt === null || this.stepViewLogged.has( key ) ) {

			return;
		}

		this.stepViewLogged.add( key );
		setTimeout( () => this.logEvent( 'step_viewed', {}, stepName ), 0 );
	}

	/**
	 * Session context that arrives after form_loaded (DEV-7892): the partner
	 * from the customer lookup, the countries load time.
	 */
	public logContextUpdated( session: Record<string, unknown>, timings?: Record<string, number> ): void {

		this.logEvent( 'context_updated', {
			...( Object.keys( session ).length > 0 ? { session } : {} ),
			...( timings ? { timings } : {} ),
		} );
	}

	/**
	 * Log an 'abandoned' event for the current step when the page is hidden
	 * or unloaded (DEV-7864). Events otherwise only fire on step completion,
	 * so a visitor who stalls and leaves would emit nothing. Best effort:
	 * iOS Safari may drop a beacon sent while the page goes away.
	 *
	 * Each trigger is logged once per step visit. A tab switch fires only
	 * visibilitychange; closing the tab fires visibilitychange and then
	 * pagehide, so a real exit carries a 'pagehide' row as well (where the
	 * browser delivers it). Coming back to the tab re-arms both, so leaving
	 * again is logged again (event_seq orders them). The last 'abandoned' row
	 * of a session with no later event is where the visitor left.
	 */
	private listenForAbandonment(): void {

		if( typeof window === 'undefined' || typeof document === 'undefined' ) {

			return;
		}

		document.addEventListener( 'visibilitychange', () => {

			if( document.visibilityState === 'hidden' ) {

				this.logAbandonment( 'visibilitychange' );
			}
			else {

				this.abandonLogged.clear();
			}
		} );

		window.addEventListener( 'pagehide', () => this.logAbandonment( 'pagehide' ) );
	}

	private logAbandonment( trigger: string ): void {

		// Only pre-submission steps: after the submit the lead exists, and leaving is the expected end.
		if( ! this.formActive || this.currentFormGroupName === 'finished' || this.stepEnteredAt === null ) {

			return;
		}

		const key = this.stepEnteredAt + ':' + trigger;

		if( this.abandonLogged.has( key ) ) {

			return;
		}

		this.abandonLogged.add( key );

		this.logFormAction( 'abandoned', this.currentFormGroupName, this.currentFormGroup, undefined, {
			...this.stepTimings( this.currentFormGroup, false ),
			trigger,
			// Peek, not take: a tab switch is not the end of the step (DEV-7892 review)
			fields: { interactions: this.interactionTracker.peekStepCounts() },
		} );
	}

	/**
	 * Attribution sent with every telemetry event (DEV-7864). ca, fbclid and
	 * msclkid come from the controls handleStep0Data() fills from the URL or
	 * cookies. UTM tags come from the landing-page query the landing_query
	 * cookie holds (the source DEV-7584 settled on for the landing query),
	 * else from the form URL; a source counts only with a non-empty
	 * utm_source. Each value is cut to 255 chars.
	 */
	private telemetryAttribution(): Record<string, string> {

		const control = ( name: string ): string => String( this.mainForm.controls[name]?.value ?? '' );

		const own = new URLSearchParams( window.location.search );
		const landing = new URLSearchParams( control( '_landing_query' ) );
		const utmFrom = landing.get( 'utm_source' ) ? landing : own;

		const out: Record<string, string> = {
			ca:      control( '_ca' ),
			fbclid:  control( '_fbclid' ),
			msclkid: control( '_msclkid' ),
		};

		for( const tag of [ 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content' ] ) {

			out[tag] = utmFrom.get( tag ) ?? '';
		}

		for( const key of Object.keys( out ) ) {

			out[key] = out[key].slice( 0, 255 );
		}

		return out;
	}

	/**
	 * DEV-6558: emit the submitted third-party answers as telemetry, as entered
	 * (DEV-7892, see TelemetryService.thirdPartyValues). Distinct from the
	 * DEV-6554 `questions_submit_success` button-click event — this one carries
	 * the answer data.
	 */
	public logThirdPartyAnswersSubmitted( questionKey: number ): void {

		this.logFormAction(
			'questions_submitted',
			'thirdPartyQuestions',
			undefined,
			this.telemetryService.thirdPartyValues( this.thirdPartyForm, questionControlKeys( this.thirdPartyForm, questionKey ) )
		);
	}

	public skipVerification(): void {

		// Covers both the footer Skip button and the verification component's
		// own skip handler, which delegates here.
		this.logFormAction( 'otp_skip', 'verification' );
		this.trackEventWithContext( 'Skip', 'verification' );
		this.proceedPostSubmitFlow();
	}

	public simulatePostSubmitFlow() {

		this.formActive = false;
		this.formLoading = false;
		this.questionsActive = false;
		this.aboutToRedirect = false;
		this.failSafeMessage = false;
		this.questions = undefined;
		this.currentQuestionIndex = 0;
		this.lastResubmitPhoneTime = null;
		this.token = 'debug_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
		this.currentFormGroupName = 'finished';

		// Set a random phone number for testing
		const randomPhone = '06 ' + Math.floor( Math.random() * 900 + 100 ) + ' ' + Math.floor( Math.random() * 900 + 100 ) + ' ' + Math.floor( Math.random() * 90 + 10 );
		(<UntypedFormGroup>this.mainForm.controls['nameEmailPhone']).controls['phoneNumberComplete'].setValue( randomPhone );

		if( this.getSetting('verificationEnabled') ) {

			this.setupVerificationForm();
			this.postSubmitStep = 'verification';
			return;
		}

		this.postSubmitStep = 'thankYou';
	}

	public setupVerificationForm() {

		this.lastResubmitPhoneTime = null;
		this.verificationForm.controls['_token'].setValue( this.token );
		this.verificationForm.controls['otp'].reset();

		// Telemetry: the OTP verification step is now displayed / the code has
		// been sent (DEV-6558). No FormGroup — the OTP must never be logged.
		this.logFormAction( 'verification_shown', 'verification' );
	}

	// Setup for the third party questions
	public setupThirdPartyForm() {

		if( this.questions ) {

			// DEV-6874: display order (Health Insurance first). Done before anything
			// renders, as QuestionComponent indexes `questions` by display position.
			this.questions = orderThirdPartyQuestions( this.questions );
			this.currentQuestionIndex = 0;

			var keys = [];

			// Loop over questions
			for( var question of this.questions ) {

				var questionKey = question.key;
				keys.push( question.key );

				// Add controls based on questions
				this.thirdPartyForm.addControl(
					'question_' + questionKey, new UntypedFormControl( '2' ) ) ;

				// Loop over the extra questions (max 3)
				for( var extraQuestion of [1,2,3] ) {

					var subQuestionKey = 'extra_' + extraQuestion;

					// Check if there is a sub question
					if( question[subQuestionKey + '_type' as keyof typeof question] !== "0" ) {

						var subQuestionName = 'question_' + questionKey + '_' + extraQuestion;
						this.thirdPartyForm.addControl( subQuestionName, new UntypedFormControl( '' ) );
					}
				}
			}

			this.thirdPartyForm.addControl( '_token', new UntypedFormControl( this.token ) ) ;
			this.thirdPartyForm.addControl( '_existingKeys', new UntypedFormControl( JSON.stringify( keys )))
			this.questionsActive = true;
			this.trackingService.setStepWithName( 'thirdPartyQuestions' );
			this.logQuestionShown();
		}
	}

	public get currentQuestion(): Question | undefined {

		return this.questions?.[ this.currentQuestionIndex ];
	}

	/**
	 * Telemetry: which third-party question / sub-questions the consumer was
	 * shown (DEV-6558). Emitted per step since DEV-6874, so a question only
	 * counts as shown once its step is actually displayed.
	 */
	public logQuestionShown(): void {

		const question = this.currentQuestion;

		if( ! question ) {
			return;
		}

		this.logFormAction( 'questions_shown', 'thirdPartyQuestions', undefined, {
			question_keys:  questionControlKeys( this.thirdPartyForm, question.key ),
			question_index: this.currentQuestionIndex,
			question_count: this.questions.length,
		} );
	}

	public proceedPostSubmitFlow() {

		// Affiliate mode and in-page (iframe) embeds don't redirect to the dashboard; show the fail-safe message instead
		if( this.affiliateConfigService.isAffiliateMode || (<UntypedFormGroup>this.mainForm ).controls['_isIframe'].value ) {

			this.postSubmitStep = 'thankYou';
			this.failSafeMessage = true;
			return;
		}

		if( this.questions && this.questions.length > 0 ) {

			if( ! this.questionsActive ) {

				this.setupThirdPartyForm();
			}

			this.postSubmitStep = 'thirdPartyQuestions';
			return;
		}

		this.postSubmitStep = 'thankYou';
		this.countDownAndRedirect();
	}

	// Setup for the volume calculator form
	public setupVolumeCalculatorForm( vcCategories: any ) {

		if( vcCategories ) {

			for( var [catIndex,category] of vcCategories.entries() ) {

				// Only index is used
				for( var [itemIndex, _] of category.items.entries() ) {

					var controlId = catIndex + '-' + itemIndex;

					// Add controls based on questions
					this.volumeCalculatorForm.addControl( controlId, new UntypedFormControl( 0 ) ) ;
				}
			}
		}
	}

	// To start the action of redirecting after x seconds if possible
	public countDownAndRedirect() {

		// We can only do something if we have a token
		if( typeof this.token.length === 'undefined' || this.token.length === 0 ) {

			console.warn( 'No token. Cannot Redirect' );
			this.failSafeMessage = true;
			return;
		}

		// Primary: use dashboard_url from postRequest response; append token
		var dashboardUrl : string;
		if( this.responseDashboardUrl.length > 0 ) {

			var base = this.responseDashboardUrl;
			var separator = base.indexOf('?') >= 0 ? '&' : '?';
			dashboardUrl = base + separator + 'id=' + this.token;
		}
		else {

			dashboardUrl = 'https://sirelo.org/personal-dashboard/?id=' + this.token;
		}

		// Set dashboard url for the button and redirect
		this.dashboardUrl = dashboardUrl;

		// In seconds
		var waitingTime = 5;

		// Make sure the beforeunload is gone
		removeEventListener("beforeunload", this.beforeUnloadListener, {capture: true});

		// The URL itself is not logged: it carries the per-lead capability token in
		// `?id=`, and Bugsnag records console output as a breadcrumb (DEV-7386).
		console.log( 'Everything is in order. Starting count down for the dashboard redirect.' );

		// Show the message of redirecting the user shortly
		this.aboutToRedirect = true;

		// Telemetry: the automatic post-submit redirect to the consumer
		// dashboard (DEV-6453) — the final conversion step. sendBeacon
		// survives the imminent navigation.
		this.logFormAction( 'dashboard_redirect', 'thankYou' );

		setTimeout( () => {

			// Get token and redirect
			window.location.href = this.dashboardUrl;

		}, waitingTime * 1000 );
	}

	private verifyDynamicSteps() {

		// Check for enabling residence
		if( this.getSetting('residenceEnabled') && ! this.getSetting('residenceIntEnabled') ) {

			// The logic to show the propertyTypeDetailsFrom
			if( this.currentFormGroupName === 'propertyTypeFrom' ) {

				// This might change the next form group
				this.verifyPropertyTypeStep( 'from' );
			}

			if( this.currentFormGroupName === 'movingTo' ) {

				this.verifyMovingTo();
			}

			if( this.currentFormGroupName === 'propertyTypeTo' ) {

				// This might change the next form group
				this.verifyPropertyTypeStep( 'to' );
			}
		}

		// Check for movingContent or movingBoxes
		if( this.currentFormGroupName === 'movingSize' ) {

			this.verifyMovingSize();
		}

		// DEV-6936: both countries are known once movingTo is done, so the
		// email-relay opt-in checkbox can be decided here. Outside the
		// residenceEnabled block above — the relay rollout is unrelated to it.
		if( this.currentFormGroupName === 'movingTo' ) {

			this.verifyEmailRelayOptIn();
		}

		if( this.domainData?.preMatchEnabled ) {

			// If next step
			if( this.currentFormGroup.controls['_next'].value === 'movingDate' ) {

				// Skip askPrematch if _customerId has a value
				if (!this.mainForm.controls['_customerId'].value) {
					// In this function we will check if we actually should do the prematch
					this.askPrematch();
				}
			}
		}

	}

	public back() {

		this.backMainForm();
	}

	public backMainForm() {

		var formGroup = this.currentFormGroup;

		// Record time spent on current step before leaving
		this.recordStepTime( formGroup );

		// Fire-and-forget telemetry for the back navigation (DEV-6453), tagged
		// on the step we are leaving. Emitted before any mutation hides it.
		this.logFormAction( 'back', this.currentFormGroupName, formGroup, undefined, {
			...this.stepTimings( formGroup, true ),
			fields: { interactions: this.interactionTracker.takeStepCounts() },
		} );

		// Clear this. just in case
		this.showNote = false;

		var prevFormGroupName = formGroup.controls['_previous'].value;
		var prevFormGroup = this.mainForm.get( prevFormGroupName ) as UntypedFormGroup;

		this.currentStep--;
		this.currentFormGroup = prevFormGroup;
		this.currentFormGroupName = prevFormGroupName;

		formGroup.controls['_shouldShow'].setValue( false );
		prevFormGroup.controls['_shouldShow'].setValue( true );

		window.scrollTo(0,0);

		if( 'parentIFrame' in window ) {

			// @ts-ignore
			parentIFrame.sendMessage( 'scroll-to-top-of-iframe' );
		}

		this.emitStepChange( this.currentFormGroupName );

		// Start timer for step we navigated back to
		this.stepEnteredAt = Date.now();

		this.logStepViewed();
	}

	// Function to show a specific component by formGroupName
    public shouldShow(formGroupName: string): boolean {

        return (<UntypedFormGroup>this.mainForm.controls[formGroupName]).controls['_shouldShow'].value;
    }

	private launchExitConfirmation() {

		// Store the listener
		this.beforeUnloadListener = (event: any ) => {
			event.preventDefault();

			// This is the same as the preventDefault for some browsers
			// This text is most likely not visible for anybody (or old browsers maybe)
			return event.returnValue = 'Are you sure you want to exit?';
		};

		var setupOnload = false;
		this.mainForm.valueChanges.subscribe( () => {

			if( ! setupOnload ) {

				setupOnload = true;
				addEventListener("beforeunload", this.beforeUnloadListener, {capture: true});
			}
		} );
	}

	public verifyPostcodeApi() {

		// Enabled for this domain or not
		if( ! this.getSetting('postcodeApiEnabled') )
			return false;

		// Check if we got a postcode but nothing else

		var movingFrom = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;

		// Return if (or)
		// - Address has a value
		// - City has a value
		// - Country has a value and it's not NL
		var addressHasValue = movingFrom.controls['address'].value && movingFrom.controls['address'].value.length > 0;
		var cityHasValue = movingFrom.controls['city'].value && movingFrom.controls['city'].value.length > 0;
		var countryHasValueAndIsNotNetherlands = movingFrom.controls['country'].value && movingFrom.controls['country'].value.length > 0 && this.verifyCountryCode( movingFrom.controls['country'].value ) !== 'NL';

		if( addressHasValue || cityHasValue || countryHasValueAndIsNotNetherlands )
			return false;

		// We good, attempt to launch api

		return true;
	}

	public verifyCountryCode( countryCode: string ) : string {

		var alternativeCountryCodes = {
			'GB1' : 'GB', // England
			'GB2' : 'GB', // Scotland
			'GB3' : 'GB', // Wales
			'NL1' : 'NL', // Holland
			'CW1' : 'CW', // Netherlands Antilles
			'US1' : 'US',
		}

		if( countryCode in alternativeCountryCodes ) {

			return alternativeCountryCodes[countryCode as keyof typeof alternativeCountryCodes];
		}

		return countryCode;
	}

	public verifyTabIndex( formGroupName: string ) {

		// Check if form group is active
		return (<UntypedFormGroup>this.mainForm.get( formGroupName ) ).controls['_shouldShow'].value ? 0 : -1;
	}

	public autoFillProtection( formGroupName: string ) {

		return (<UntypedFormGroup>this.mainForm.get( formGroupName ) ).controls['_shouldShow'].value ? 'on' : 'absolutely-not';
	}

	/**
	 * Retrieves the correct order for the address fields on movingFrom and movingTo
	 * Can be requested specifically for a certain situation
	 * The specific situation relies on the environment file having the values
	 * If it's not there, it will fall back to the default
	 * @param specific
	 */
	public getFieldOrder( specific: string = 'both' ) {

		// Decide order based on domainData
		var fieldOrder = this.getSetting('addressFieldOrder');

		// Only if specific movingTo is requested
		if( specific === 'movingTo' ) {

			var fieldOrderTo = this.getSetting('addressFieldOrderTo');

			if( fieldOrderTo && fieldOrderTo.length > 0 ) {

				fieldOrder = fieldOrderTo;
			}
		}

		return fieldOrder ?? ['address', 'city', 'postcode'];
	}

	public getFieldOrderByCountry( country: string, specific: string = 'both' ) {

		var fieldOrder;

		// Clean country
		country = this.verifyCountryCode( country );

		// Grab country data
		var selectedCountryData = countryData[country as keyof typeof countryData];

		// If there's no country data. Fallback
		if( ! selectedCountryData ) {

			return this.getFieldOrder( specific );
		}

		// Default selection
		var keyToSelect = 'fieldOrder';

		// If the movingTo order is requested
		if( specific === 'movingTo' ) {

			// Grab field order
			keyToSelect = 'fieldOrderTo';

			fieldOrder = selectedCountryData[keyToSelect as keyof typeof selectedCountryData];

			console.log( 'selecting fieldOrderTo from country ' + country );
			console.log( fieldOrder );

			if( ! Array.isArray(fieldOrder) || fieldOrder.length <= 0 ) {

				console.log( 'fallback to field order' );

				fieldOrder = selectedCountryData["fieldOrder" as keyof typeof selectedCountryData];
			}
		}
		else {

			fieldOrder = selectedCountryData[keyToSelect as keyof typeof selectedCountryData];
		}

		console.log( 'Field order:', fieldOrder );

		// Check if that exists otherwise fallback
		return Array.isArray(fieldOrder) && fieldOrder.length > 0 ? fieldOrder : ['address', 'city', 'postcode'];
	}

	public getVolumeOrder() {

		var type =
			typeof this.domainData?.volumeType === 'string' && this.domainData.volumeType.length > 0
				? this.domainData.volumeType
				: 'both';

		if( type === 'both' ) {

			return ['m3','divider','ft3'];
		}
		else if( type === 'both_reversed' ) {

			return ['ft3','divider','m3'];
		}
		else if ( type === 'ft3' ) {

			return ['ft3'];
		}
		else {

			return ['m3'];
		}
	}

	public getFloorLevels(): any {

		return [
			{
				value: 1,
				label: $localize `:{floor-level-0}:0`,
			},
			{
				value: 5,
				label: '1',
			},
			{
				value: 6,
				label: '2',
			},
			{
				value: 7,
				label: '3',
			},
			{
				value: 8,
				label: '4',
			},
			{
				value: 9,
				label: '5',
			},
			{
				value: 11,
				label: '6+',
			},
		];
	}

	public getVariationTranslation( type: string ) {

		var output = '';

		if( type === 'label-address' ) {

			// Also variation 1 | default
			output = $localize `:{address-label}:Address`;

			if( this.locale.substring( 0, 2 ) === 'en' ) {

				// Check if there is a variation
				let addressVariation = this.getSetting('translationVariations')?.en?.address;

				if( addressVariation ) {

					// List of possible variations
					// This should only include the variations
					// If one language file needs 2 variations
					// E.g. en-US uses both Address and Street address
					let variations = {
						// 1 Is default Address
						2: 'Street address',
						3: 'Halloo',
						// .. add more variations if needed
					}

					let searchVariation = variations?.[addressVariation as keyof typeof variations];

					if( addressVariation > 1 && searchVariation ) {

						output = searchVariation;
					}
				}
			}
		}
		else if( type === 'label-postcode' ) {

			// Also variation 1 | default
			output = $localize `:{postcode-label}:Postcode`;

			if( this.locale.substring( 0, 2 ) === 'en' ) {

				// Check if there is a variation
				let postcodeVariation = this.getSetting('translationVariations')?.en?.postcode;

				if( postcodeVariation ) {

					// List of possible variations
					// This should only include the variations
					// If one language file needs 2 variations
					// E.g. en-US uses both Postcode and ZIP code/Post code
					let variations = {
						// 1 Is default Postcode
						2: 'ZIP code/Post code',
						3: 'Postal code',
						// .. add more variations if needed
					}

					let searchVariation = variations?.[postcodeVariation as keyof typeof variations];

					if( postcodeVariation > 1 && searchVariation ) {

						output = searchVariation;
					}
				}
			}
		}

		return output;
	}


	// === PROPERTY TYPE CODE === //

	private initialPropertyTypeCheck() {

		// Check if residence is enabled on this website
		if( this.getSetting('residenceEnabled') ) {

			this.enablePropertyTypeFrom();
		}

		// ======================= //
		// CHECK FOR INT RESIDENCE //
		// ======================= //

		if( this.getSetting('residenceIntEnabled') && ! this.getSetting('residenceEnabled') ) {

			this.enablePropertyTypeFrom();
		}
	}

	// Check if we have to show the propertyTypeDetails step
	public verifyPropertyTypeStep( fromOrTo: string ) {

		// Set direction
		var direction = ( fromOrTo === 'from' ? 'From' : 'To' );

		// If propertyType is 2
		var propertyType = (<UntypedFormGroup>this.mainForm.get( 'propertyType' + direction ) ).controls['propertyType'].value;

		// Get current state
		var wasDetailsActive = this.mainForm.controls['_propertyTypeDetails' + direction + 'Enabled'].value;

		// If is apartment/flat and details were not active
		if( propertyType === '2' && wasDetailsActive === false ) {

			if( fromOrTo === 'from' ) {

				this.enablePropertyTypeDetailsFrom();
			}
			else {

				this.enablePropertyTypeDetailsTo();
			}
		}
		// property type house and details were active then reset stuff
		else if( propertyType === '1' && wasDetailsActive === true ) {

			if( fromOrTo === 'from' ) {

				this.disablePropertyTypeDetailsFrom();
			}
			else {

				this.disablePropertyTypeDetailsTo();
			}
		}
	}

	// This enables or disables propertyTypeTo
	private verifyMovingTo() {

		// Check if lead is national
		var fromVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value );
		var toVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value );

		// Also check if movingTo street & zipcode have a value
		var movingToAddress = (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['address'].value ?? '';
		var movingToPostcode = (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['postcode'].value ?? '';
		var hasAddressAndPostcode = ( movingToAddress.length > 0 && movingToPostcode.length > 0 );

		var wasPropertyTypeToEnabled = (<UntypedFormGroup>this.mainForm ).controls['_propertyTypeToEnabled'].value;

		// National lead and if it has address and postcode
		if( fromVal === toVal && hasAddressAndPostcode ) {

			// If it wasn't enabled before
			if( ! wasPropertyTypeToEnabled ) {

				// Enable it
				this.enablePropertyTypeTo();
			}
		}
		else {

			// Check if it was enabled
			if( wasPropertyTypeToEnabled ) {

				// Disable propertyTypeTo
				this.disablePropertyTypeTo();
			}
		}
	}

	private enablePropertyTypeFrom() {

		console.log( 'Enable PropertyTypeFrom' );

		(<UntypedFormGroup>this.mainForm ).controls['_propertyTypeFromEnabled'].setValue( true );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['movingFrom'] ).controls['_next'].setValue( 'propertyTypeFrom' );
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_previous'].setValue( 'propertyTypeFrom' );

		if( ! this.getSetting('hidePropertyTypeQuestion') ) {

			// Do not make propertyTypeQuestion required if it's not showing
			(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['propertyType'].addValidators( Validators.required );
		}

		// Add one to total step amount
		this.totalStepsAmount++;
	}

	private enablePropertyTypeDetailsFrom() {

		console.log( 'Enable PropertyTypeDetailsFrom' );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['_next'].setValue( 'propertyTypeDetailsFrom' );
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_previous'].setValue( 'propertyTypeDetailsFrom' );

		// Add one to total step amount
		this.totalStepsAmount++;

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['floor'].addValidators( Validators.required );

		this.mainForm.controls['_propertyTypeDetailsFromEnabled'].setValue( true );
	}

	private disablePropertyTypeDetailsFrom() {

		console.log( 'Disable PropertyTypeDetailsFrom' );

		// Reset next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['_next'].setValue( 'movingTo' );
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_previous'].setValue( 'propertyTypeFrom' );

		// Minus 1 total steps
		this.totalStepsAmount--;

		// Disable propertyTypeDetails
		this.mainForm.controls['_propertyTypeDetailsFromEnabled'].setValue( false );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['floor'].removeValidators( Validators.required );

		// Reset values for propertyTypeDetails
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['floor'].reset();
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['elevator'].reset();
	}

	private enablePropertyTypeTo() {

		console.log( 'Enable PropertyTypeTo' );

		(<UntypedFormGroup>this.mainForm ).controls['_propertyTypeToEnabled'].setValue( true );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_next'].setValue( 'propertyTypeTo' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'propertyTypeTo' );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['propertyType'].addValidators( Validators.required );

		// Add one to total step amount
		this.totalStepsAmount++;
	}

	private disablePropertyTypeTo() {

		console.log( 'Disable PropertyTypeTo' );

		// Disable propertyTypeDetailsTo first
		this.disablePropertyTypeDetailsTo();

		// Disable the propertyTypeTo
		(<UntypedFormGroup>this.mainForm ).controls['_propertyTypeToEnabled'].setValue( false );

		// Reset next and prev
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_next'].setValue( 'movingSize' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'movingTo' );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['propertyType'].removeValidators( Validators.required );

		// Subtract one from total steps
		this.totalStepsAmount--;
	}

	private enablePropertyTypeDetailsTo() {

		console.log( 'Enable PropertyTypeDetailsTo' );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['_next'].setValue( 'propertyTypeDetailsTo' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'propertyTypeDetailsTo' );

		// Add one to total step amount
		this.totalStepsAmount++;

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['floor'].addValidators( Validators.required );

		this.mainForm.controls['_propertyTypeDetailsToEnabled'].setValue( true );
	}

	private disablePropertyTypeDetailsTo() {

		// If it was disabled already
		if( ! this.mainForm.controls['_propertyTypeDetailsToEnabled'].value ) {

			return;
		}

		console.log( 'Disable PropertyTypeDetailsTo' );

		// Reset next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['_next'].setValue( 'movingSize' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'propertyTypeTo' );

		// Minus 1 total steps
		this.totalStepsAmount--;

		// Disable propertyTypeDetails
		this.mainForm.controls['_propertyTypeDetailsToEnabled'].setValue( false );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['floor'].removeValidators( Validators.required );

		// Reset values for propertyTypeDetails
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['floor'].reset();
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['elevator'].reset();
	}

	// === MovingSize ===
	public verifyMovingSize() {

		var movingSize = (<UntypedFormGroup>this.mainForm.controls['movingSize'] );

		// Check what the next step will be
		if( movingSize.controls['movingSize'].value !== 4 ) {

			// Enable movingContent
			this.enableMovingContent();
		}
		else {

			// Enable movingBoxes
			this.enableMovingBoxes();
		}
	}

	private enableMovingContent() {

		// Set the next step for moving size
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_next'].setValue( 'movingContent' );

		// Skip upload step – go directly to movingDate
		(<UntypedFormGroup>this.mainForm.controls['movingContent'] ).controls['_next'].setValue( 'movingDate' );
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_previous'].setValue( 'movingContent' );

		const wasUploadEnabled = this.mainForm.controls['_uploadPicturesEnabled'].value;
		this.mainForm.controls['_uploadPicturesEnabled'].setValue( false );
		if( wasUploadEnabled ) {

			this.totalStepsAmount--;
		}
	}

	private enableMovingBoxes() {

		// Set the next step for movingSize step
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_next'].setValue( 'movingBoxes' );

		const baggagePhotosEnabled = !!this.getSetting( 'baggagePhotosEnabled' );

		if( baggagePhotosEnabled ) {

			// Include upload step in chain
			(<UntypedFormGroup>this.mainForm.controls['uploadPictures'] ).controls['_previous'].setValue( 'movingBoxes' );
			(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_previous'].setValue( 'uploadPictures' );

			if( ! this.mainForm.controls['_uploadPicturesEnabled'].value ) {

				this.totalStepsAmount++;
			}
			this.mainForm.controls['_uploadPicturesEnabled'].setValue( true );
		}
		else {

			// Skip upload step – go directly to movingDate
			(<UntypedFormGroup>this.mainForm.controls['movingBoxes'] ).controls['_next'].setValue( 'movingDate' );
			(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_previous'].setValue( 'movingBoxes' );
		}
	}

	// === GET SETTING WITH LANGUAGE FALLBACK === //
	public getSetting(settingName: string): any {

		// Get current language code
		const currentLanguage = this.locale.split('-')[0];
		const currentLocale = this.locale;

		// First, try to get the setting from full locale-specific configuration (e.g., "pt-BR")
		if( this.domainData?.['config-by-language']?.[currentLocale]?.[settingName] !== undefined ) {

			return this.domainData['config-by-language'][currentLocale][settingName];
		}

		// Then, try to get the setting from language-specific configuration (e.g., "pt")
		if( this.domainData?.['config-by-language']?.[currentLanguage]?.[settingName] !== undefined ) {

			return this.domainData['config-by-language'][currentLanguage][settingName];
		}

		// Fall back to domain-level configuration
		return this.domainData?.[settingName];
	}

	/**
	 * DEV-6936: whether the email-relay opt-in checkbox should appear on the
	 * contact step. Gated on the move itself — origin and destination country —
	 * never on the website or domain: the same move gets the same answer wherever
	 * the form is embedded.
	 *
	 * The two together give the move type, which is the axis the ERP splits its
	 * own eligibility on: a domestic move (origin = destination) needs the origin
	 * country in email-relay.json's "national" list, an international move needs
	 * it in "international". Those two lists mirror the ERP's
	 * EMAIL_RELAY_ENABLED_COUNTRIES and EMAIL_RELAY_ENABLED_INTERNATIONAL_COUNTRIES
	 * and MUST be kept in sync with them — the browser cannot read the ERP env, and
	 * offering "mask my email address" where no proxy address gets provisioned
	 * breaks a promise made to the consumer.
	 *
	 * Still excluded: the mover-profile funnel, which is a funnel distinction
	 * rather than a website one.
	 */
	private isEmailRelayOptInVisible(): boolean {

		const fromCode = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value );
		const toCode = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value );

		if( ! fromCode || ! toCode ) {

			return false;
		}

		const isNationalMove = fromCode === toCode;
		const enabledCountries: string[] = isNationalMove
			? emailRelayCountries.national
			: emailRelayCountries.international;

		const isMainFunnel = ! this.mainForm.controls['_customerId'].value;

		return enabledCountries.includes( fromCode ) && isMainFunnel;
	}

	/**
	 * DEV-6936: keep _emailRelayOptInVisible (and with it the pre-checked state of
	 * emailRelayOptIn) in step with the visibility rules. Called from
	 * verifyDynamicSteps() when leaving movingTo, the first point where both
	 * country values are known — going back to change a country and forward again
	 * necessarily passes through movingTo, so this recomputes on its own.
	 *
	 * Only acts on a change in visibility, so passing movingTo repeatedly does not
	 * silently re-check a box the consumer deliberately unchecked. When the checkbox
	 * disappears the opt-in resets to false, so a hidden control never submits true.
	 */
	private verifyEmailRelayOptIn(): void {

		const group = this.mainForm.get( 'nameEmailPhone' ) as UntypedFormGroup;
		const wasVisible = !! group.controls['_emailRelayOptInVisible'].value;
		const isVisible = this.isEmailRelayOptInVisible();

		if( isVisible === wasVisible ) {

			return;
		}

		group.controls['_emailRelayOptInVisible'].setValue( isVisible );

		// Shown pre-checked (the recommended default), cleared when hidden.
		group.controls['emailRelayOptIn'].setValue( isVisible );
	}

	/**
	 * Build tracking context for Mixpanel events. Includes requestToken, form values, and step times.
	 */
	public getTrackingContext(): Record<string, unknown> {

		const nameEmailPhone = this.mainForm.get( 'nameEmailPhone' ) as UntypedFormGroup | null;
		const movingFrom = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup | null;
		const movingTo = this.mainForm.get( 'movingTo' ) as UntypedFormGroup | null;
		const movingSize = this.mainForm.get( 'movingSize' ) as UntypedFormGroup | null;

		const stepTimes: Record<string, number> = {};
		const stepGroupNames = [
			'movingFrom', 'propertyTypeFrom', 'propertyTypeDetailsFrom',
			'movingTo', 'propertyTypeTo', 'propertyTypeDetailsTo',
			'movingSize', 'movingContent', 'movingBoxes',
			'movingDate', 'consentMovers', 'nameEmailPhone'
		];

		for( const name of stepGroupNames ) {

			const group = this.mainForm.get( name ) as UntypedFormGroup | null;
			if( group?.contains( '_timespent' ) ) {

				stepTimes[name] = group.controls['_timespent'].value ?? 0;
			}
		}

		return {
			requestToken: this.token ?? '',
			countryFrom: movingFrom?.get( 'country' )?.value ?? '',
			countryTo: movingTo?.get( 'country' )?.value ?? '',
			movingSize: movingSize?.get( 'movingSize' )?.value ?? '',
			name: nameEmailPhone?.get( 'name' )?.value ?? '',
			email: nameEmailPhone?.get( 'email' )?.value ?? '',
			telephone: nameEmailPhone?.get( 'phoneNumberComplete' )?.value ?? nameEmailPhone?.get( 'phoneNumber' )?.value ?? '',
			stepTimes
		};
	}

	/**
	 * Track event with form context (requestToken, form values, step times) merged in.
	 */
	public trackEventWithContext( eventName: string, step: string, properties?: Record<string, unknown> ): void {

		const context = this.getTrackingContext();
		this.trackingService.trackEvent( eventName, step, { ...context, ...properties } );
	}

	// === M2 FIELD === //

	private initialM2FieldCheck() {

		// If propertyTypeFrom is not enabled
		// M2 can only be active if propertyType is enabled
		// PropertyTypeFrom will be enabled or disabled before this function starts.
		// propertyType will not be disabled again after it being enabled
		// We follow the same strategy for the m2 field
		if( ! this.mainForm.controls['_propertyTypeFromEnabled'].value ) {

			return;
		}

		if( this.getSetting('m2FieldEnabled') ) {

			console.log( 'Enable m2 field' );
			this.mainForm.controls['_m2Enabled'].setValue( true );
			(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['m2'].addValidators( Validators.required );
		}
	}

	// === MovingTo Check for extended === //

	private initialMovingToExtendedCheck() {

		if( this.getSetting('movingToExtendedEnabled') ) {

			var movingTo = this.mainForm.get( 'movingTo' ) as UntypedFormGroup;

			// If is not extended, meaning we have no data from step 0 for address and postcode
			if( ! movingTo.controls['_isExtended'].value ) {

				// Extend it but optional
				// Note: They are always optional, but with the _extendedOptional they also get (optional) in the label
				// They do not get the (optional) label in the case of being extended when step0 movingTo data is filled in
				movingTo.controls['_isExtended'].setValue( true );
				movingTo.controls['_extendedOptional'].setValue( true );
				this.movingToExtendedOptionalLabelsStartingValue = true;

				console.log( 'Enabling movingTo Extended' );
			}
		}
	}

	// === MovingTo fields extended checking === //

	public checkToEnableMovingToExtended( isInitialCall = false ) {

		const makeMovingToRequiredOrOptional = ( what: string ) => {

			if( what === 'required' ) {

				// optional fields go required
				// When we make this optional again, we know the original value from movingToExtendedOptionalLabelsStartingValue
				movingTo.controls['_extendedOptional'].setValue( false );

				console.log( 'movingTo address & postcode REQUIRED' );

				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].addValidators( Validators.required );
				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].updateValueAndValidity();

				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].addValidators( Validators.required );
				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].updateValueAndValidity();
			}
			else {

				console.log( 'movingTo address & postcode OPTIONAL' );

				// Required fields go optional
				// These only go required when countryFrom is the same as countryTo
				// On the initial call, skip the reset to preserve any URL pre-filled values
				if( !isInitialCall ) {

					console.log( 'movingTo address & postcode CLEARED' );

					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].reset();
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].reset();
				}
				else {

					console.log( 'movingTo address & postcode NOT cleared (initial call)' );
				}

				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].removeValidators( Validators.required );
				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].updateValueAndValidity();

				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].removeValidators( Validators.required );
				(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].updateValueAndValidity();

				// This variable is true when we had the (optional) label in the first place
				// The (optional) label gets added in the first place when the movingTo extended is enabled through the env setting and not the step0 data
				if( this.movingToExtendedOptionalLabelsStartingValue ) {

					// If we need to reset the labels to get (optional)
					movingTo.controls['_extendedOptional'].setValue( true );
				}
			}
		}

		var movingFrom = (<UntypedFormGroup>this.mainForm.get( 'movingFrom' ) );
		var movingTo = (<UntypedFormGroup>this.mainForm.get( 'movingTo' ) );

		var countryFromValue = movingFrom.controls['country'].value;
		var countryToValue = movingTo.controls['country'].value;

		// In this case we want to check if the countries are the same
		// Check if we have a value in movingFrom so they are both not empty in the working scenario
		if( ( countryFromValue && countryFromValue.length > 0 ) && countryFromValue === countryToValue ) {

			// Check if it's not extended already
			if( ! movingTo.controls['_isExtended'].value ) {

				console.log( 'movingTo Extended ENABLED' );

				// Set extended
				movingTo.controls['_isExtended'].setValue( true );
			}

			// We only have the movingToExtendedEnabled for Sirelo's
			// For SEA sites we want to keep the movingTo address & postcode optional
			if( this.getSetting('movingToExtendedEnabled') ) {

				// Make it required
				makeMovingToRequiredOrOptional( 'required' );
			}
			else {

				// (optional) labels
				movingTo.controls['_extendedOptional'].setValue( true );
			}
		}
		else {

			// Country values are not the same now or already

			// If it is enabled
			// We only want to disable things if they were actually enabled
			if( movingTo.controls['_isExtended'].value ) {

				// If it's not enabled through the env setting
				if( ! this.getSetting('movingToExtendedEnabled') ) {

					// Disable it
					movingTo.controls['_isExtended'].setValue( false );

					console.log( 'Extended movingTo DISABLED' );
				}

				makeMovingToRequiredOrOptional( 'optional' );
			}
		}

	}

	// === HOUSE SIZE FIELD === //
	private initialPropertyRoomsFieldCheck() {

		if( ! this.mainForm.controls['_propertyTypeFromEnabled'].value ) {

			return;
		}

		var propertyRoomsFieldEnabled = false;

		// Check if propertyRooms is enabled
		if( this.getSetting('propertyRoomsEnabled') ) {

			propertyRoomsFieldEnabled = true;
		}

		if( propertyRoomsFieldEnabled ) {

			console.log( 'Enable `propertyRooms` field' );
			this.mainForm.controls['_propertyRoomsEnabled'].setValue( true );
			(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['propertyRooms'].addValidators( Validators.required );
		}
	}

	// === PREMATCH === //

	public moversConsentUpdate$ = new Subject<void>();

	private askPrematch() {

		// Only consider origin country (moving From)
		var fromVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value );

		var allowedOrigins = this.domainData?.preMatchOriginCountries;
		var limitByOriginCountry = Array.isArray( allowedOrigins ) && allowedOrigins.length > 0;

		var isEligibleCountry = limitByOriginCountry
			? allowedOrigins.includes( fromVal )
			: true;

		if( ! isEligibleCountry ) {

			this.disableConsentMovers();

			return;
		}

		// Enable the step
		this.enableConsentMovers();

		// Add loading here
		this.fccLoading = true;

		// Should not be allowed to click continue while still loading

		console.log( 'Country is Eligible for prematch' );

		// Put the request in here
		var body = JSON.stringify( this.mainForm.value );

		// Track how long the prematch request takes
		var prematchStart = Date.now();

		// Without movers the consent step must be skipped, or the visitor sees it empty.
		// Clear the movers too, so an earlier reply's selection is not submitted.
		const prematchFailed = () => {

			this.fccLoading = false;
			( this.mainForm.get( 'consentMovers.movers' ) as UntypedFormArray ).clear();
			( this.mainForm.get( 'consentMovers._originalMovers' ) as UntypedFormArray ).clear();
			this.disableConsentMovers();
			this.mainForm.controls['_fccEnabled'].setValue( false );
		};

		// Send data to ERP
		this.apiService.postAskPrematch(body).subscribe( {
			next: (movers) => {

				var prematchDuration = Date.now() - prematchStart;
				this.mainForm.controls['_prematchDuration'].setValue( prematchDuration );

				// response has reply
				console.log( 'reply' );
				console.log( movers );

				const moversArray = parsePrematchMovers( movers );

				// DEV-7522: while the ERP is being deployed, the reply arrives as a 200 with
				// an HTML error page or an empty body. Not reported: the visitor continues
				// without the step and the lead is processed as usual.
				if( moversArray === null ) {

					console.warn( 'ask-prematch did not return a mover list' );

					this.logPrematchFailed( { reason: 'no_list', response: responseStart( movers ) }, prematchDuration );

					prematchFailed();

					return;
				}

				// Get both form arrays
				var moversFormArray = this.mainForm.get( 'consentMovers.movers' ) as UntypedFormArray;
				var originalMoversFormArray = this.mainForm.get( 'consentMovers._originalMovers' ) as UntypedFormArray;

				// Clear both arrays
				moversFormArray.clear();
				originalMoversFormArray.clear();

				// Create the form
				moversArray.forEach( (mover: Mover) => {

					// originalMovers gets its own group with the same initial values
					moversFormArray.push( this.buildMoverGroup( mover ) );
					originalMoversFormArray.push( this.buildMoverGroup( mover ) );
				});

				// If the mover array has items, enable consent movers
				if( moversArray.length > 0 ) {

					// Emit an event to notify the consent movers component to update
					this.moversConsentUpdate$.next();

					// Set _fccEnabled to true
					this.mainForm.controls['_fccEnabled'].setValue( true );
				}
				else {

					this.logPrematchFailed( { reason: 'empty' }, prematchDuration );
					prematchFailed();
				}
			},
			error: (error) => {

				var prematchDuration = Date.now() - prematchStart;
				this.mainForm.controls['_prematchDuration'].setValue( prematchDuration );

				console.error('Error in askPrematch:', error);

				this.logPrematchFailed( { reason: 'error', ...failureDetails( error, prematchDuration ) }, prematchDuration );

				// Disable consent movers on error. complete() does not run after an error,
				// so prematchFailed() also stops the loading animation.
				prematchFailed();
			},
			complete: () => {
				// Stop loading animation in both success and error cases
				this.fccLoading = false;
			}
		});
	}

	/**
	 * Single source of truth for the shape of a mover FormGroup.
	 * Used for both consentMovers.movers and consentMovers._originalMovers, and the
	 * modal rebuilds its own copy from these values - so a new mover field only ever
	 * needs adding here.
	 */
	private buildMoverGroup( mover: Mover ): UntypedFormGroup {

		return new UntypedFormGroup({
			checkbox: new UntypedFormControl( mover.checked ),
			_id: new UntypedFormControl( mover.id ),
			_name: new UntypedFormControl( mover.name ),
			_logo: new UntypedFormControl( mover.logo ),
			_rating: new UntypedFormControl( mover.rating ),
			_amount: new UntypedFormControl( mover.rating_amount ),
			_city: new UntypedFormControl( mover.city ),
			_verified: new UntypedFormControl( !! mover.verified )
		});
	}

	private enableConsentMovers() {

		// If consentMovers is already enabled
		if( this.mainForm.controls['_consentMoversEnabled'].value ) {

			console.log( 'Consent Movers step was already enabled' );

			return;
		}

		console.log( 'Enable Consent Movers step' );

		// Set the next step for moving date to the consent step
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_next'].setValue( 'consentMovers' );

		// Set the previous step for the name email phone to consent step
		(<UntypedFormGroup>this.mainForm.controls['nameEmailPhone'] ).controls['_previous'].setValue( 'consentMovers' );

		// Add one to total step amount
		this.totalStepsAmount++;

		// Validator is added after the consentMovers component has mover data

		// Set to enabled
		(<UntypedFormGroup>this.mainForm ).controls['_consentMoversEnabled'].setValue( true );
	}

	private disableConsentMovers() {

		if( ! this.mainForm.controls['_consentMoversEnabled'].value ) {

			console.log( 'Consent Movers step was already disabled' );

			return;
		}

		if( this.currentFormGroupName === 'consentMovers' ) {
			this.next( true );
		}

		// Reset the next step for moving date to the name email phone step
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_next'].setValue( 'nameEmailPhone' );

		// Reset the previous step for the name email phone to consent step
		(<UntypedFormGroup>this.mainForm.controls['nameEmailPhone'] ).controls['_previous'].setValue( 'movingDate' );

		// Subtract one to total step amount
		this.totalStepsAmount--;

		// Remove the validator from the consentMovers form group
		const consentMovers = this.mainForm.get('consentMovers') as UntypedFormGroup;
		consentMovers.clearValidators();
		consentMovers.updateValueAndValidity();

		// Set to disabled
		(<UntypedFormGroup>this.mainForm ).controls['_consentMoversEnabled'].setValue( false );
	}

	// === SET COUNTRY BY TIMEZONE === //
	private setCountryByTimezone() {
		
		// === PREFILL MOVING FROM === //

		// Check if movingFrom country already has a value
		var movingFrom = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;
		var currentCountryValue = movingFrom.controls['country'].value;

		if( currentCountryValue && currentCountryValue.length > 0 ) {

			// Country already has a value, don't override
			return;
		}

		// Get current timezone
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		if( timezone === "" || !timezone ) {

			return;
		}

		// Get country code from timezone
		const timezoneInfo = timezoneData[timezone as keyof typeof timezoneData];

		if( !timezoneInfo || !timezoneInfo.c || timezoneInfo.c.length === 0 ) {

			return;
		}

		// Get the first country code from the array
		const countryCode = timezoneInfo.c[0];

		if( countryCode ) {

			console.log( 'Setting country by timezone:', timezone, '->', countryCode );

			// Set the country value
			movingFrom.controls['country'].setValue( countryCode );
			movingFrom.controls['country'].markAsDirty();
		}
	}

}
